import { useState } from "react";
import { toast } from "sonner";
import { X, Radio } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function AuthModal() {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  if (!authOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back to the arena.");
      } else {
        await register(email, password, name);
        toast.success("Account created. Ready to predict.");
      }
      setAuthOpen(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md fade-in" data-testid="auth-modal">
      <div className="relative w-full max-w-md tile p-8 grain">
        <button onClick={() => setAuthOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="auth-close-button">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Radio className="w-5 h-5 text-[#0B0F17]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-2xl uppercase tracking-tight">
              {mode === "login" ? "Enter Arena" : "Join Oracle"}
            </div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Predict every over. Climb the ladder.</div>
            {mode === "register" && (
              <button
                type="button"
                onClick={() => setMode("login")}
                className="mt-1 text-xs text-amber-400 hover:text-amber-300 hover:underline lowercase tracking-wide"
                data-testid="quick-signin-link"
              >
                Or I just want to sign in …
              </button>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <label className="section-label">Display Name</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                required minLength={1} maxLength={40}
                className="mt-1 w-full bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-amber-500/60 text-white"
                placeholder="Virat K."
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="section-label">Email</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1 w-full bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-amber-500/60 text-white"
              placeholder="you@stadium.com"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="section-label">Password</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              className="mt-1 w-full bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-amber-500/60 text-white"
              placeholder="Oracle1234$"
              data-testid="auth-password-input"
            />
          </div>

          <button type="submit" disabled={busy} className="btn-primary w-full mt-2" data-testid="auth-submit-button">
            {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-slate-400">
          {mode === "login" ? (
            <>New to The Cricket Pitch Oracle?{" "}
              <button onClick={() => setMode("register")} className="text-amber-400 font-semibold hover:underline" data-testid="switch-to-register">Create account</button>
            </>
          ) : (
            <>Already a member?{" "}
              <button onClick={() => setMode("login")} className="text-amber-400 font-semibold hover:underline" data-testid="switch-to-login">Sign in</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
